import React, { useContext, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Divider
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Parser from 'html-react-parser';
import { UserContextType, useAuth } from '../auth';
import { contentProofreadingService } from '../service/ContentProofreadingService';
import { ProofreadContentType } from '../types/proofread';

const Emitter = require('tiny-emitter/instance');

interface AiRewriteButtonProps {
  content: string;
  contentType: ProofreadContentType;
  // Rich-text editors pass true so the markup is kept and the preview renders it.
  isHtml?: boolean;
  onAccept: (rewritten: string) => void;
  disabled?: boolean;
}

const isBlank = (content: string, isHtml: boolean) =>
  (isHtml ? content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : content).trim().length === 0;

// "AI Rewrite" button + side-by-side review dialog, the same flow ManageAnnouncements uses for
// descriptions, packaged so any editor (custom pages, groups) can drop it next to its field.
const AiRewriteButton: React.FC<AiRewriteButtonProps> = ({
  content, contentType, isHtml = false, onAccept, disabled
}) => {
  const { getToken } = useContext<UserContextType>(useAuth);
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewritten, setRewritten] = useState('');
  const [open, setOpen] = useState(false);

  const handleRewrite = async () => {
    setIsRewriting(true);
    try {
      const result = await contentProofreadingService.rewrite(content, contentType, isHtml, getToken ? getToken() : '');
      setRewritten(result);
      setOpen(true);
    } catch (err) {
      Emitter.emit('notify', 'Failed to get an AI rewrite. Please try again.', 'error');
    } finally {
      setIsRewriting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setRewritten('');
  };

  const handleAccept = () => {
    onAccept(rewritten);
    handleClose();
  };

  const render = (text: string) =>
    isHtml ? <Box sx={{ '& p': { mt: 0 } }}>{Parser(text)}</Box> : <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{text}</Typography>;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AutoFixHighIcon />}
        onClick={handleRewrite}
        disabled={disabled || isRewriting || isBlank(content, isHtml)}
      >
        {isRewriting ? 'Rewriting...' : 'AI Rewrite'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>AI Rewrite</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>Original</Typography>
          <Box sx={{ mb: 2, color: 'text.secondary' }}>{render(content)}</Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Rewritten</Typography>
          {render(rewritten)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Keep Original</Button>
          <Button onClick={handleAccept} variant="contained" color="primary">
            Use Rewritten Version
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AiRewriteButton;
